
import React from 'react';
import { FormControl, FormField, FormItem, FormMessage } from '@/components/ui/form';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { HelpCircle, Link } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider } from '@/components/ui/tooltip';
import TagsInput from '../TagsInput';
import { EnhancedLabel, EnhancedTooltipTrigger } from '@/lib/shadcn-wrappers';
import NTBIntegration from '../../NTBIntegration';
import HashtagGenerationButton from '../../../production/HashtagGenerationButton';


const InterviewContentSection = ({ form }) => {
  // Helper function to check if URL is NTB article
  const isNTBArticle = (url: string) => {
    return url && url.startsWith('ntb:');
  };
  // Extract a human-readable title from NTB context JSON
  const getNtbTitle = (ctx: string) => {
    try {
      const ntb = JSON.parse(ctx);
      return ntb?.title || 'NTB-artikkel';
    } catch {
      const first = (ctx || '').split('\n').find(l => l.trim()) || 'NTB-artikkel';
      return first.length <= 200 ? first : 'NTB-artikkel';
    }
  };

  // Watch context and contextUrl values for display
  const contextValue = form.watch('context');
  const contextUrlValue = form.watch('contextUrl');

  return (
    <div className="border rounded-md p-4 pb-5">
      <h3 className="text-lg font-semibold mb-4">Intervjuinnhold</h3>
      <div className="space-y-4">
        {/* NTB Integration */}
        <NTBIntegration 
          onArticleSelect={(article) => {
            form.setValue('title', article.title);
            form.setValue('context', article.context);
            form.setValue('contextUrl', article.contextUrl);
            if (article.contextImageUrl) {
              form.setValue('contextImageUrl', article.contextImageUrl);
            }
            if (article.contextImageCaption) {
              form.setValue('contextImageCaption', article.contextImageCaption);
            }
          }}
          className="mb-4"
        />


        {/* Manual context URL field for external links */}
        <FormField
          control={form.control}
          name="contextUrl"
          render={({ field }) => (
            <FormItem>
              <div className="flex items-center gap-2">
                <EnhancedLabel>Kontekst URL for ekstern artikkel (valgfri)</EnhancedLabel>
                <TooltipProvider>
                  <Tooltip>
                    <EnhancedTooltipTrigger asChild>
                      <span className="cursor-help">
                        <HelpCircle className="h-4 w-4 text-muted-foreground" />
                      </span>
                    </EnhancedTooltipTrigger>
                    <TooltipContent className="max-w-80">
                      <p>Legg til en ekstern URL. Hvis du vil bruke NTB, bruk NTB-integrasjonen over.</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              <div className="flex items-center gap-2">
                <FormControl>
                  <div className="relative w-full">
                    <Input 
                      placeholder="https://example.com/article" 
                      {...field}
                      onChange={(e) => {
                        field.onChange(e);
                        // If manual URL is added, set context
                        if (e.target.value && !e.target.value.startsWith('ntb:')) {
                          form.setValue('context', `Ekstern artikkel: ${e.target.value}`);
                        }
                        // If URL is cleared and it's not an NTB article, clear context
                        else if (!e.target.value) {
                          const currentContext = form.getValues('context');
                          if (currentContext && currentContext.startsWith('Ekstern artikkel:')) {
                            form.setValue('context', '');
                          }
                        }
                      }}
                      className="pl-8"
                    />
                    <Link className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                  </div>
                </FormControl>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Hvis du legger til en URL, vil konteksten være klikkbar i artikkelen og åpne den eksterne siden.
              </p>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Hidden context field to maintain form schema compatibility */}
        <FormField
          control={form.control}
          name="context"
          render={({ field }) => (
            <input type="hidden" {...field} />
          )}
        />
        
        <FormField
          control={form.control}
          name="question"
          render={({ field }) => (
            <FormItem>
              <EnhancedLabel>Spørsmål</EnhancedLabel>
              <FormControl>
                <Textarea 
                  placeholder="Spørsmålet som ble stilt"
                  className="min-h-[100px]"
                  {...field} 
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        
        <FormField
          control={form.control}
          name="answer"
          render={({ field }) => (
            <FormItem>
              <EnhancedLabel>Svar</EnhancedLabel>
              <FormControl>
                <Textarea 
                  placeholder="Svaret på spørsmålet"
                  className="min-h-[200px]"
                  {...field} 
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        
        <FormField
          control={form.control}
          name="tags"
          render={({ field }) => (
            <FormItem>
              <div className="flex items-center justify-between mb-2">
                <EnhancedLabel>Emneord (tags)</EnhancedLabel>
                <HashtagGenerationButton
                  question={form.watch('question') || ''}
                  answer={form.watch('answer') || ''}
                  onHashtagsGenerated={(hashtags) => {
                    console.log('[DEBUG] Generated hashtags received:', hashtags);
                    field.onChange(hashtags);
                  }}
                />
              </div>
              <FormControl>
                <TagsInput 
                  value={field.value} 
                  onChange={(newTags) => {
                    console.log('[DEBUG] TagsInput onChange called with:', newTags);
                    field.onChange(newTags);
                  }}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
    </div>
  );
};

export default InterviewContentSection;
